import axios from 'axios'

export type UsedToWorkAtCompaniesMentionedReq = {
    companyNames: string[]
}

export type UsedToWorkAtCompaniesMentionedRes = {
    companyResolutionData: {
        inputCompanyName: string
        resolvedCompanies: {
            companyId: string
            companyName: string
        }[]
    }[]
}

export type TargetJobLocationMention = {
    city?: string
    country?: string
}

export type TargetJobLocationsMentionedReq = {
    locations: TargetJobLocationMention[]
}

export type TargetJobLocationsMentionedRes = {
    locationMentions: {
        locationMentionId: string
        city: string | null
        country: string | null
    }[]
}

export type TargetJobTitlesMentionedReq = {
    jobTitles: string[]
}

export type TargetJobTitlesMentionedRes = {
    targetJobTitleMentions: {
        targetJobTitleMentionId: string
        jobTitle: string
    }[]
}

export type TargetSalaryCurrency = 'GBP' | 'USD' | 'EUR'

export type TargetSalaryMentionedReq = {
    minSalary?: number
    maxSalary?: number
    currency: TargetSalaryCurrency
}

export type TargetSalaryMentionedRes = {
    targetSalaryMention: {
        targetSalaryMentionId: string
        minSalaryGBP: number | null
        maxSalaryGBP: number | null
        minSalaryUSD: number | null
        maxSalaryUSD: number | null
        minSalaryEUR: number | null
        maxSalaryEUR: number | null
        inputCurrency: TargetSalaryCurrency
    }
}

export type ExecuteJobSearchReq = {
    activeOnly?: boolean
    minSalary?: number
    maxSalary?: number
    includeMissingSalary?: boolean
}

export type ExecuteJobSearchRes = {
    jobSearchExecutionId: string
    jobs: {
        coreSignalJobId: string
        coreSignalId: string
        title: string | null
        location: string | null
        companyName: string | null
        coreSignalJobCreatedAt: string | null
        rankScore: number | null
    }[]
    totalPreviewResults: number
    generatedJobTitles: string[]
}

export type GiveJobsFeedbackStatus = 'Interested' | 'Not Interested'

export type GiveJobsFeedbackEntry = {
    coreSignalJobId: string
    status: GiveJobsFeedbackStatus
    reason: string
}

export type GiveJobsFeedbackReq = {
    jobFeedbacks: GiveJobsFeedbackEntry[]
}

export type GiveJobsFeedbackRes = {
    updatedCoreSignalJobIds: string[]
}

class KitToolApi {
    private static instance: KitToolApi

    public constructor() {
        if (KitToolApi.instance) {
            return KitToolApi.instance
        }

        KitToolApi.instance = this
    }

    public async usedToWorkAtCompaniesMentioned(
        req: UsedToWorkAtCompaniesMentionedReq,
        options: { apiKey: string; convId: string },
    ): Promise<UsedToWorkAtCompaniesMentionedRes> {
        const url = new URL(
            '/kit/used-to-work-at-companies-mentioned',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as UsedToWorkAtCompaniesMentionedRes
    }

    public async targetJobLocationsMentioned(
        req: TargetJobLocationsMentionedReq,
        options: { apiKey: string; convId: string },
    ): Promise<TargetJobLocationsMentionedRes> {
        const url = new URL(
            '/kit/target-job-locations-mentioned',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as TargetJobLocationsMentionedRes
    }

    public async targetJobTitleMentioned(
        req: TargetJobTitlesMentionedReq,
        options: { apiKey: string; convId: string },
    ): Promise<TargetJobTitlesMentionedRes> {
        const url = new URL(
            '/kit/target-job-title-mentioned',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as TargetJobTitlesMentionedRes
    }

    public async targetSalaryMentioned(
        req: TargetSalaryMentionedReq,
        options: { apiKey: string; convId: string },
    ): Promise<TargetSalaryMentionedRes> {
        const url = new URL(
            '/kit/target-salary-mentioned',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as TargetSalaryMentionedRes
    }

    public async executeJobSearch(
        req: ExecuteJobSearchReq,
        options: { apiKey: string; convId: string },
    ): Promise<ExecuteJobSearchRes> {
        const url = new URL(
            '/kit/execute-job-search',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as ExecuteJobSearchRes
    }

    public async giveJobsFeedback(
        req: GiveJobsFeedbackReq,
        options: { apiKey: string; convId: string },
    ): Promise<GiveJobsFeedbackRes> {
        const url = new URL(
            '/kit/give-jobs-feedback',
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        const response = await axios.post(url.toString(), req, {
            withCredentials: true,
            headers: {
                'x-api-key': options.apiKey,
                conv_id: options.convId,
            },
        })

        return response.data as GiveJobsFeedbackRes
    }
}

const kitToolApi = new KitToolApi()
export default kitToolApi
